import { QueryKey as CacheKey } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FolderContentsView, SPACE_FOLDER_PAGE_SIZE, SpaceNodeSortBy } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { Order } from '../../../types/general.types';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { useBrowserPages } from './use-browser-pages.hook';
import { FolderTreeScope } from './use-space-navigation-folders.hook';

type Props = {
  // Off while the table shows something else, a search's results: nothing is fetched, and what the
  // cache holds shows again when it is on.
  enabled?: boolean;
  folderId: null | string;
  // Which side of the module is reading: the owner's space routes, or the recipient's room route.
  scope: FolderTreeScope;
  // The column and its direction; the server sorts, so every page continues the order of the one
  // before it.
  sortBy: SpaceNodeSortBy;
  sortOrder: Order;
  // Null for the Vault: its folders are not a space tree and come from the navigation endpoint.
  space: null | TreeSpaceRef;
};

// Where the folder sits in the query key; the sort comes after it.
const FOLDER_KEY_LENGTH = 6;

// The top of a space has its own route: its hidden root folder never leaves the server.
const contentsRoutePath = (space: TreeSpaceRef, folderId: null | string) =>
  folderId ? [space.id, 'folders', folderId, 'contents'] : [space.id, 'contents'];

// A recipient reads one room through one route; the folder is a parameter, not a path segment,
// because the room is what the guard resolved and everything else hangs off it.
const contentsRequest = (
  scope: FolderTreeScope,
  space: TreeSpaceRef,
  folderId: null | string,
  queryParams: Record<string, number | string>,
) =>
  scope === FolderTreeScope.RECIPIENT
    ? ApiService.get<FolderContentsView>(
        { endpoint: BackendRoute.MY_DATA_ROOMS, routePath: [space.id, 'contents'] },
        { queryParams: { ...queryParams, ...(folderId ? { folderId } : {}) } },
      )
    : ApiService.get<FolderContentsView>(
        {
          endpoint: SPACE_BACKEND_ROUTE[space.kind],
          routePath: contentsRoutePath(space, folderId),
        },
        { queryParams },
      );

// A folder 50 items at a time, sub-folders first; each "Load more" asks for the next 50.
export const useSpaceFolderContents = ({
  enabled = true,
  folderId,
  scope,
  sortBy,
  sortOrder,
  space,
}: Props) => {
  // Only keys the cache: the route takes the company from the session.
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryKey = useMemo(
    () => [
      QueryKey.GET_SPACE_FOLDER_CONTENTS,
      companyId,
      scope,
      space?.kind,
      space?.id,
      folderId,
      sortBy,
      sortOrder,
    ],
    [companyId, folderId, scope, sortBy, sortOrder, space?.id, space?.kind],
  );

  const fetchPage = useCallback(
    (skip: number) => {
      if (!space) throw new Error('A space tree is required to list a folder');

      return contentsRequest(scope, space, folderId, {
        skip,
        sortBy,
        sortOrder,
        take: SPACE_FOLDER_PAGE_SIZE,
      });
    },
    [folderId, scope, sortBy, sortOrder, space],
  );

  // A new order of the same folder keeps the rows on screen until the sorted ones arrive;
  // another folder starts empty rather than showing this one.
  const keepsRowsOf = useCallback(
    (previousKey: CacheKey) =>
      queryKey.slice(0, FOLDER_KEY_LENGTH).every((part, index) => part === previousKey[index]),
    [queryKey],
  );

  return useBrowserPages({
    enabled: enabled && !!space && !!companyId,
    failureMessage: 'FOLDER_CONTENTS_LOAD_FAILED',
    fetchPage,
    keepsRowsOf,
    queryKey,
  });
};
